import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { CalendarIcon, CheckIcon, Clock, User, Mail, Briefcase, Check, List } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormCheckbox } from "@/components/FormFields/FormCheckbox";
import { cn } from "@/lib/utils";

import {
  discoverySessionSchema,
  DiscoverySessionFormData,
  practiceAreas,
  preferredTimes,
  roleOptions,
  timezones,
} from "./discoverySessionSchema";

const WEBHOOK_URL = import.meta.env.VITE_DISCOVERY_WEBHOOK_URL;

const DiscoverySessionForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPracticeAreas, setSelectedPracticeAreas] = useState<string[]>([]);

  const form = useForm<DiscoverySessionFormData>({
    resolver: zodResolver(discoverySessionSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      firmName: "",
      role: "",
      practiceAreas: [],
      challenges: "",
      goals: "",
      timezone: "",
      consent: false,
    },
  });

  const onSubmit = async (data: DiscoverySessionFormData) => {
    setIsSubmitting(true);

    try {
      if (!WEBHOOK_URL) {
        throw new Error("Discovery webhook URL not configured - please set VITE_DISCOVERY_WEBHOOK_URL in your environment variables");
      }
      // Prepare the JSON data
      const jsonData = {
        ...data,
        preferredDate: format(data.preferredDate, "yyyy-MM-dd"),
        submittedAt: new Date().toISOString(),
      };

      console.log("Submitting data to webhook:", jsonData);

      // Make the fetch request with explicit JSON content type
      const response = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(jsonData),
      });

      if (response.ok) {
        toast.success("Your discovery session has been scheduled!", {
          description: "You'll receive a confirmation email shortly.",
        });
        form.reset();
        setSelectedPracticeAreas([]);
      } else {
        console.error("Webhook response error:", response.status, response.statusText);
        throw new Error(`Failed to submit form: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to schedule your session", {
        description: "Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePracticeArea = (area: string) => {
    if (selectedPracticeAreas.includes(area)) {
      const newAreas = selectedPracticeAreas.filter(a => a !== area);
      setSelectedPracticeAreas(newAreas);
      form.setValue("practiceAreas", newAreas);
    } else {
      const newAreas = [...selectedPracticeAreas, area];
      setSelectedPracticeAreas(newAreas);
      form.setValue("practiceAreas", newAreas);
    }
  };

  return (
    <div className="bg-card/30 backdrop-blur-sm p-6 md:p-8 rounded-xl border border-white/10 shadow-lg">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input 
                        placeholder="Enter your first name" 
                        className="pl-10" 
                        {...field} 
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Last Name */}
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input 
                        placeholder="Enter your last name" 
                        className="pl-10" 
                        {...field} 
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Work Email */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Work Email</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input 
                      placeholder="name@yourfirm.com" 
                      className="pl-10" 
                      type="email"
                      {...field} 
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Firm Name */}
          <FormField
            control={form.control}
            name="firmName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Firm Name</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input 
                      placeholder="Enter your firm name" 
                      className="pl-10" 
                      {...field} 
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Role */}
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Your Role</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {roleOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Practice Areas */}
          <FormField
            control={form.control}
            name="practiceAreas"
            render={() => (
              <FormItem>
                <FormLabel>Practice Areas</FormLabel>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-1.5">
                  {practiceAreas.map((area) => (
                    <div 
                      key={area.value}
                      onClick={() => togglePracticeArea(area.value)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-all",
                        selectedPracticeAreas.includes(area.value) 
                          ? "bg-primary/20 border border-primary/40" 
                          : "bg-secondary/50 border border-border hover:bg-secondary"
                      )}
                    >
                      <div className={cn(
                        "w-4 h-4 flex items-center justify-center rounded border",
                        selectedPracticeAreas.includes(area.value) 
                          ? "bg-primary border-primary" 
                          : "border-muted"
                      )}>
                        {selectedPracticeAreas.includes(area.value) && (
                          <CheckIcon className="h-3 w-3 text-primary-foreground" />
                        )}
                      </div>
                      <span className="text-sm">{area.label}</span>
                    </div>
                  ))}
                </div>
                {form.formState.errors.practiceAreas && (
                  <p className="text-sm font-medium text-destructive mt-2">
                    {form.formState.errors.practiceAreas.message}
                  </p>
                )}
              </FormItem>
            )}
          />

          {/* Challenges */}
          <FormField
            control={form.control}
            name="challenges"
            render={({ field }) => (
              <FormItem>
                <FormLabel>What challenges are you currently facing around AI adoption?</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Tell us about any specific challenges or concerns you have with implementing AI in your practice..." 
                    className="min-h-[100px]" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Goals */}
          <FormField
            control={form.control}
            name="goals"
            render={({ field }) => (
              <FormItem>
                <FormLabel>What are your goals for this session?</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="What specific outcomes would make this discovery session valuable for you?" 
                    className="min-h-[100px]" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Preferred Date */}
            <FormField
              control={form.control}
              name="preferredDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Preferred Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Select a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) => {
                          // Disable past dates and weekends
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const day = date.getDay();
                          return date < today || day === 0 || day === 6;
                        }}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Preferred Time */}
            <FormField
              control={form.control}
              name="preferredTime"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preferred Time</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <div className="flex items-center">
                          <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                          <SelectValue placeholder="Select a time" />
                        </div>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {preferredTimes.map((time) => (
                        <SelectItem key={time.value} value={time.value}>
                          {time.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Timezone */}
          <FormField
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Timezone</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your timezone" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {timezones.map((timezone) => (
                      <SelectItem key={timezone.value} value={timezone.value}>
                        {timezone.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Consent Checkbox */}
          <FormCheckbox
            form={form}
            name="consent"
          >
            Yes, I'd like to receive relevant updates and session details from Docket Labs. <a href="/privacy-policy" className="text-primary hover:underline">Privacy policy</a>.
          </FormCheckbox>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white py-6 text-lg font-medium"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <div className="animate-spin mr-2 h-4 w-4 border-2 border-t-transparent border-white rounded-full"></div>
                <span>Scheduling...</span>
              </div>
            ) : (
              <span>Schedule My Free Session</span>
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default DiscoverySessionForm;
